import { WidgetContext } from 'Pages/Widget/Widget.constants';
import { WidgetActionType } from 'Pages/Widget/Widget.store';
import { useGetBookingPayload } from 'Pages/Widget/Widget.utils';
import { useCallback, useContext, useState, useEffect } from 'react';
import { useAppDispatch } from 'hooks/hooks';
import { useGetClientCreditCardInfoQuery } from 'store/client/client.api';
import { usePayMutation, useGetClientSecretMutation, widgetApi } from 'store/widget/widget.api';
import { CreateLeadBody } from 'store/widget/widget.types';
import { CardElement, useElements, useStripe } from '@stripe/react-stripe-js';
import toast from 'react-hot-toast';
import { isServerErrorWithMessage, isStripeErrorMessage } from 'shared/ErrorTypeCheck';
import useTranslation from 'shared/useTranslation';
import {
  createCardSetupCache,
  createPriceChangePrompt,
  handlePayOutcome,
  PriceChangePromptState,
  runPaySequence,
} from './paySequence';
import { getInitCreditCardIds } from './utils';

export const useSetupNewCard = () => {
  const stripe = useStripe();
  const elements = useElements();
  const [getClientSecret] = useGetClientSecretMutation();
  const [translations] = useTranslation([
    'widget.step.payment.failed.create.payment.method',
    'widget.step.payment.something.is.wrong',
    'widget.step.payment.stripe.is.not.available',
  ] as const);

  return useCallback(async (): Promise<string> => {
    const cardEl = elements?.getElement(CardElement);

    if (!stripe || !elements || !cardEl) {
      throw new Error(translations.widgetStepPaymentStripeIsNotAvailable);
    }

    const stripeResponse = await stripe.createPaymentMethod({
      type: 'card',
      card: cardEl,
    });

    const { error, paymentMethod } = stripeResponse;
    if (error || !paymentMethod) {
      throw new Error(translations.widgetStepPaymentFailedCreatePaymentMethod);
    }

    const response = await getClientSecret(paymentMethod.id).unwrap();
    const clientSecret = response.client_secret;
    const result = await stripe?.confirmCardSetup(clientSecret);

    if (result.error) {
      const { message } = result.error;
      if (message) {
        throw new Error(message);
      }
      throw new Error(translations.widgetStepPaymentSomethingIsWrong);
    }
    return paymentMethod.id;
  }, [
    elements,
    getClientSecret,
    stripe,
    translations.widgetStepPaymentFailedCreatePaymentMethod,
    translations.widgetStepPaymentSomethingIsWrong,
    translations.widgetStepPaymentStripeIsNotAvailable,
  ]);
};

export const useProceedPayment = ({ onError }: { onError?: (error: unknown) => void }) => {
  const [translations] = useTranslation(['common.form.unsuccessful', 'widget.step.summary.card.required'] as const);
  const { state, dispatch } = useContext(WidgetContext);
  const appDispatch = useAppDispatch();
  const [pay] = usePayMutation();
  const setupNewCard = useSetupNewCard();
  const [priceChange, setPriceChange] = useState<PriceChangePromptState | null>(null);
  const [priceChangePrompt] = useState(() =>
    createPriceChangePrompt({
      invalidateQuote: () => appDispatch(widgetApi.util.invalidateTags(['PriceQuote'])),
      showPrompt: setPriceChange,
    }),
  );
  const [cardSetupCache] = useState(createCardSetupCache);

  const submission = useGetBookingPayload(state);

  const proceedPayment = useCallback(
    async (selectedPaymentCardId: string) => {
      if (!submission) {
        return;
      }

      try {
        const outcome = await runPaySequence({
          payload: submission.body,
          priceCents: submission.priceCents,
          selectedCardId: selectedPaymentCardId,
          setupNewCard: () => cardSetupCache.setUpOnce(setupNewCard),
          pay: (body: CreateLeadBody) => pay(body).unwrap(),
          confirmPriceChanged: priceChangePrompt.ask,
        });

        handlePayOutcome(outcome, {
          onPaid: () => dispatch({ type: WidgetActionType.GO_TO_PAYMENT_SUCCESS }),
          onCardRequired: () => toast.error(translations.widgetStepSummaryCardRequired),
        });
      } catch (error) {
        cardSetupCache.forget();
        onError?.(error);

        if (isStripeErrorMessage(error)) {
          toast.error(error.message);
          return;
        }

        if (isServerErrorWithMessage(error)) {
          toast.error(error.data.message);
          return;
        }
        toast.error(translations.commonFormUnsuccessful);
      }
    },
    [
      cardSetupCache,
      dispatch,
      onError,
      pay,
      priceChangePrompt,
      setupNewCard,
      submission,
      translations.commonFormUnsuccessful,
      translations.widgetStepSummaryCardRequired,
    ],
  );

  return {
    proceedPayment,
    isSubmissionReady: submission !== null,
    priceChange,
    priceChangeActions: priceChangePrompt.actions,
    forgetSetUpCard: cardSetupCache.forget,
  };
};

export const useGetPaymentCards = () => {
  const { data: cards = [], isFetching } = useGetClientCreditCardInfoQuery();
  const [addedCardId, setAddedCardId] = useState<string | null>(null);
  const initialDefaultCard = getInitCreditCardIds(cards, addedCardId);

  const [selectedPaymentCardId, setSelectedPaymentCardId] = useState(initialDefaultCard);

  useEffect(() => {
    if (cards.length > 0 && !isFetching) {
      setSelectedPaymentCardId(initialDefaultCard);
    }
  }, [cards, initialDefaultCard, isFetching]);

  const onCardSelect = (id: string) => {
    setSelectedPaymentCardId(id);
  };

  return {
    initialDefaultCard,
    isFetching,
    paymentCards: cards,
    selectedPaymentCardId,
    onCardSelect,
    onCardAdded: setAddedCardId,
  };
};
